import React from "react";
import { format, isBefore } from "date-fns";
import _camelCase from "lodash/camelCase";
import { DOMPurifyWithTargetBlankHook, IconTextLink } from "component-library";
import { Trans, useTranslation } from "i18n";
import { dataTestIds } from "data-testids";
import {
  DatesFormat,
  ISelfTapeRequestStatus,
  MediaRequestType,
} from "shared-types";
import {
  IAttachment,
  ISubmissionMedia,
  IVideoRequired,
} from "shared/models/selfTapeRequest";
import {
  getTranslationDateAndTime,
  getTranslationDateAndTimeFromTagmin,
} from "shared/helpers/translationHelpers";
import SelfTapeAcceptRejectBlock from "./SelfTapeAcceptRejectBlock";
import { SelfTapeRequestInfoSubmission } from "./SelfTapeRequestInfoSubmission/SelfTapeRequestInfoSubmission";
import StatusInfoBlock from "./StatusInfoBlock";
import "./SelfTapeRequestInfo.scss";

export interface ISelfTapeRequestInfoProps {
  description: string;
  submissionDate: string;
  videosRequired: IVideoRequired[];
  isTagminUser: boolean;
  anyAmountOfVideos: boolean;
  submissionMediaList: ISubmissionMedia[];
  attachments: IAttachment[];
  isPublished: boolean;
  openResponseModal: () => void;
  selfTapeRequestStatus: ISelfTapeRequestStatus;
  performerReplyTimestamp?: string;
  performerReplyComments?: string;
  performerReplyBy?: string;
  mediaRequestType: MediaRequestType;
  selfTapeRequestExpiryDate: string;
  isAgent?: boolean;
  requestToClientComments?: string;
  requestToClientSentBy?: string;
  forwardedToPerformerTimestamp?: string;
  forwardedToTagminTimestamp?: string;
  requestToTagminSentBy?: string;
  submissionBy?: string;
}

export enum UserTypeTransKey {
  Agent = "agent",
  Performer = "performer",
}

export const SelfTapeRequestInfo: React.FC<
  React.PropsWithChildren<ISelfTapeRequestInfoProps>
> = ({
  description,
  submissionDate,
  videosRequired,
  isTagminUser,
  anyAmountOfVideos,
  submissionMediaList,
  attachments,
  isPublished,
  openResponseModal,
  selfTapeRequestStatus,
  performerReplyTimestamp,
  performerReplyComments,
  performerReplyBy,
  mediaRequestType,
  selfTapeRequestExpiryDate,
  requestToClientComments,
  requestToClientSentBy,
  isAgent,
  forwardedToPerformerTimestamp = "",
  forwardedToTagminTimestamp = "",
  requestToTagminSentBy = "",
  submissionBy = "",
}) => {
  const { t } = useTranslation();

  const mediaRequestTransKey = _camelCase(MediaRequestType[mediaRequestType]);
  const userTransKey = isAgent
    ? UserTypeTransKey.Agent
    : UserTypeTransKey.Performer;
  const mediaRequired = t(
    `common:mediaRequest.label.${mediaRequestTransKey}.required`
  );
  const isExpired = isBefore(new Date(selfTapeRequestExpiryDate), new Date());
  const isAwaiting = selfTapeRequestStatus === ISelfTapeRequestStatus.Awaiting;
  const isAccepted = selfTapeRequestStatus === ISelfTapeRequestStatus.Accepted;
  const isResponseReceived =
    selfTapeRequestStatus === ISelfTapeRequestStatus.ResponseReceived;

  const showAcceptRejectMessage =
    isAgent || (!isAgent && !requestToClientSentBy) || isResponseReceived;
  const showSentToClientMessage = isAccepted && requestToClientSentBy;
  const showRequestToTagminMessage =
    isAccepted && requestToTagminSentBy && isAgent;

  return (
    <section className="c-self-tape-request-info g-inner-grid g-col-md-12 text-small break-all py-5">
      <div className="c-self-tape-request-info__description g-col-lg-8 g-col-md-8">
        <h3 className="c-self-tape-request-info__title">
          {t("common:selfTapes.request.description.title")}
        </h3>
        <hr className="g-split-line" />
        <div className="c-self-tape-request-info__content">
          <div
            className="c-self-tape-request-info__text"
            data-testid={
              dataTestIds.nuJobsApp["SelfTapeRequest.Info.description"]
            }
            dangerouslySetInnerHTML={{
              __html: DOMPurifyWithTargetBlankHook.sanitize(description),
            }}
          />
        </div>
      </div>
      <div className="g-col-lg-4 g-col-md-4 c-self-tape-request-info__attachments">
        <h3 className="c-self-tape-request-info__title">
          {t("common:selfTapes.request.description.attachments")}
        </h3>
        <hr className="g-split-line" />
        <div className="c-self-tape-request-info__content">
          {attachments.map((attachment) => (
            <div key={attachment.id}>
              <IconTextLink
                text={attachment.title}
                variant="primary"
                linkUrl={attachment.url}
                iconName="icon-paperclip1"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="g-col-lg-8 g-col-md-8 c-self-tape-request-info__tape-instructions mb-6">
        <h3 className="c-self-tape-request-info__title">
          {t("common:selfTapes.label.instruction")}
        </h3>
        <hr className="g-split-line" />
        <div className="c-self-tape-request-info__content">
          <div className="c-self-tape-request-info__text g-inner-grid-12">
            <div className="c-self-tape-request-info__text-column">
              {mediaRequired}:
            </div>
            <div className="c-self-tape-request-info__text-column">
              {anyAmountOfVideos
                ? t("common:selfTapes.request.description.anyMedia")
                : videosRequired.length}
            </div>
          </div>
          {videosRequired.map((instruction, index) => (
            <div
              key={instruction.id}
              className="c-self-tape-request-info__tape-instruction c-self-tape-request-info__text g-inner-grid-12"
            >
              <div className="c-self-tape-request-info__text-column">
                {anyAmountOfVideos
                  ? t("common:selfTapes.label.instruction.colon")
                  : t(
                      `common:selfTapes.label.instruction.${mediaRequestTransKey}`,
                      { quantity: index + 1 }
                    )}
              </div>
              <div
                className="c-self-tape-request-info__text-column"
                data-testid={
                  dataTestIds.nuJobsApp["SelfTapeRequest.Info.videoInstruction"]
                }
              >
                {instruction.instructions}
              </div>
            </div>
          ))}
        </div>

        {isAwaiting && (
          <StatusInfoBlock>
            <div className="text-m">
              <Trans
                i18nKey="common:selfTapes.request.faq"
                components={{
                  a: (
                    <a
                      href="https://www.spotlight.com/news-and-advice/the-essentials/how-to-self-tape/"
                      title="FAQ link"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </div>
          </StatusInfoBlock>
        )}

        {showAcceptRejectMessage && (
          <SelfTapeAcceptRejectBlock
            selfTapeRequestStatus={selfTapeRequestStatus}
            mediaRequestType={mediaRequestType}
            performerReplyTimestamp={performerReplyTimestamp}
            performerReplyBy={performerReplyBy}
            performerReplyComments={performerReplyComments}
            openResponseModal={openResponseModal}
          />
        )}

        {isExpired && !isResponseReceived && (
          <StatusInfoBlock
            title={t("common:selfTapes.request.banner.title.expired")}
            description={t("common:selfTapes.request.banner.expired.message")}
            className="bg-color-neutral-three"
          />
        )}

        {isResponseReceived && (
          <StatusInfoBlock
            title={t("common:selfTapes.request.banner.title.responseReceived", {
              date: t(
                "common:datesFormat.date.time.at",
                getTranslationDateAndTimeFromTagmin({
                  isTagminUser,
                  date: submissionDate,
                  dateFormat: DatesFormat.dateFormatPrimary,
                  timeFormat: DatesFormat.timeFormatPrimary,
                })
              ),
              user: submissionBy,
            })}
            description={t("common:selfTapes.request.banner.completed.message")}
            className="bg-color-neutral-three"
          />
        )}

        {showSentToClientMessage && (
          <StatusInfoBlock
            title={t(
              `common:selfTapes.request.sendToClient.info.title.${userTransKey}`,
              {
                by: requestToClientSentBy,
                ...getTranslationDateAndTime({
                  date: forwardedToPerformerTimestamp,
                  dateFormat: DatesFormat.dateFormatPrimary,
                  timeFormat: DatesFormat.timeFormatPrimary,
                }),
              }
            )}
            description={t(
              `common:selfTapes.request.sendToClient.info.description.${userTransKey}`,
              {
                message: requestToClientComments,
              }
            )}
            className="bg-color-neutral-three"
          />
        )}

        {showRequestToTagminMessage && (
          <StatusInfoBlock
            title={t("common:selfTapes.request.sendToTagmin.info.title", {
              by: requestToTagminSentBy,
              ...getTranslationDateAndTime({
                date: forwardedToTagminTimestamp,
                dateFormat: DatesFormat.dateFormatPrimary,
                timeFormat: DatesFormat.timeFormatPrimary,
              }),
            })}
            description={t(
              "common:selfTapes.request.sendToTagmin.info.description"
            )}
            className="bg-color-neutral-three"
          />
        )}
      </div>

      {isPublished && submissionMediaList.length && (
        <div className="c-self-tape-request-info__media-list-container g-col-lg-8 g-col-md-8">
          <div className="c-self-tape-request-info__media-list-container-header">
            {t("common:selfTapes.request.description.sentAt", {
              time: format(
                new Date(submissionDate),
                DatesFormat.dateFormatPrimary
              ),
            })}
          </div>
          <hr className="g-split-line" />
          <div className="c-self-tape-request-info__media-list">
            {submissionMediaList.map((submissionMedia, index, { length }) => (
              <React.Fragment key={submissionMedia.id}>
                <SelfTapeRequestInfoSubmission
                  submissionMedia={submissionMedia}
                />
                {index < length - 1 ? <hr className="g-split-line" /> : null}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default SelfTapeRequestInfo;
